import { configureStore } from "@reduxjs/toolkit";
import { setupListeners } from "@reduxjs/toolkit/query";

import { api } from "../features/api";
import merchantListReducer from "../features/slices/merchant-list";
import validationReducer from "../features/error/validation-slice";
import notificationsReducer from "../features/error/notifications-slice";
import modalReducer from "../components/UI/modal/modal-slice";
import helpPanelReducer from "../components/layouts/help-panel-slice";
import holdingsSearchConditionReducer from "../features/slices/holdings-search";
import usersListReducer from "../features/slices/user-list";
import holdingsContractSearchConditionReducer from "../features/slices/holdings-affiliated-company-search";
import gwSearchConditionReducer from "../features/slices/gw-search";
import locationReducer from "../features/authentication/location-slice";
import companyNameSearchConditionReducer from "../features/slices/company-search";
import searchModalReducer from "../components/UI/modal/search-modal-slice";
import typeDataReducer from "../features/slices/type-data";
import industryConversionSearchConditionReducer from "../features/slices/industry-conversion-search";
import rateAdjustmentCaseSearchConditionReducer from "../features/slices/rate-adjustment-case-search";
import counselingSearchConditionReducer from "../features/slices/counselings-search";
import creditCardExaminationSearchConditionReducer from "../features/slices/credit-card-examination-search";
import merchantStoreSearchConditionReducer from "../features/slices/store-tab-search";
import examinationsStatusListReducer from "../features/slices/examinations-status-list";
import contractServiceConditionReducer from "../features/slices/contract-service";
import corpInfoConditionReducer from "../features/slices/corpinfo-data";
import applicationStatusListReducer from "../features/slices/application-status-list";
import gwInfoConditionReducer from "../features/slices/gw-data";
import merchantDirectDataReducer from "../features/slices/merchant-direct-data";
import rateUpdateConfirmReducer from "../features/slices/rate-update-confirm";
import bankInfoFormConditionReducer from "../features/slices/bank-info-form";
import individualHandlingListReducer from "../features/slices/individual-handling-list";
import merchantInfoInquiryMerchantTabSearchConditionReducer from "../features/slices/merchant-tab-search";
import bankAccountsListReducer from "../features/slices/bank-accounts-list";

export const store = configureStore({
  reducer: {
    // Add the generated reducer as a specific top-level slice
    [api.reducerPath]: api.reducer,
    merchantList: merchantListReducer,
    validation: validationReducer,
    notifications: notificationsReducer,
    modal: modalReducer,
    helpPanel: helpPanelReducer,
    holdingsSearchCondition: holdingsSearchConditionReducer,
    userList: usersListReducer,
    holdingsContractSearchCondition: holdingsContractSearchConditionReducer,
    gwSearchCondition: gwSearchConditionReducer,
    location: locationReducer,
    companyNameSearchCondition: companyNameSearchConditionReducer,
    searchModal: searchModalReducer,
    typeData: typeDataReducer,
    industryConversionSearchCondition: industryConversionSearchConditionReducer,
    rateAdjustmentCaseSearchCondition: rateAdjustmentCaseSearchConditionReducer,
    counselingSearchCondition: counselingSearchConditionReducer,
    creditCardExaminationSearchCondition:
      creditCardExaminationSearchConditionReducer,
    merchantStoreSearchCondition: merchantStoreSearchConditionReducer,
    merchantInfoInquiryMerchantTabSearchCondition:
      merchantInfoInquiryMerchantTabSearchConditionReducer,
    examinationsStatusList: examinationsStatusListReducer,
    contractService: contractServiceConditionReducer,
    corpInfoSlice: corpInfoConditionReducer,
    applicationStatusList: applicationStatusListReducer,
    gwInfoSlice: gwInfoConditionReducer,
    merchantDirectDataSlice: merchantDirectDataReducer,
    rateUpdateConfirmSlice: rateUpdateConfirmReducer,
    bankInfoFormConditionSlice: bankInfoFormConditionReducer,
    individualHandlingList: individualHandlingListReducer,
    bankAccountsList: bankAccountsListReducer,
  },
  // Adding the api middleware enables caching, invalidation, polling,
  // and other useful features of `rtk-query`.
  middleware: (getDefaultMiddleware) =>
    getDefaultMiddleware({
      serializableCheck: false,
    }).concat([api.middleware]),
});

// optional, but required for refetchOnFocus/refetchOnReconnect behaviors
// see `setupListeners` docs - takes an optional callback as the 2nd arg for customization
setupListeners(store.dispatch);

export type RootState = ReturnType<typeof store.getState>;
export type AppDispatch = typeof store.dispatch;
